import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Settings2, Save, Loader2 } from "lucide-react";
import { V } from "@/lib/vocab";

// The reusable "⚙️ Configure" gear used on every module page header.
export function ModuleGearButton({ onClick, testid = "module-config-gear", title = "Configure this module" }) {
  return (
    <button data-testid={testid} onClick={onClick} title={title}
      className="rounded-full bg-white border border-sand-200 p-2.5 hover:border-ink transition-colors">
      <Settings2 className="h-4 w-4 text-ink/70" />
    </button>
  );
}

// Consistent in-page slide-over that holds a module's configuration options.
export function ModuleSettingsSheet({ open, onOpenChange, title, description, children, onSave, saving, saveLabel = "Save changes", testid = "module-settings-sheet" }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto flex flex-col" data-testid={testid}>
        <SheetHeader>
          <SheetTitle className="font-heading flex items-center gap-2"><Settings2 className="h-5 w-5 text-redline-500" strokeWidth={1.75} /> {title}</SheetTitle>
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>
        <div className="flex-1 py-5 space-y-6">{children}</div>
        {onSave && (
          <SheetFooter>
            <button data-testid={`${testid}-save`} onClick={onSave} disabled={saving}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-redline-500 text-white font-semibold px-6 py-2.5 hover:bg-redline-600 transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {saving ? "Saving…" : saveLabel}
            </button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

// A ready-made module gear body: renames the module community-wide (writes tenant.<labelKey>
// via PUT /tenants/{id}/config). Used across module pages for a consistent config surface.
export function ModuleRenameSheet({ open, onOpenChange, tenantId, tenant, refreshTenants, labelKey, defaultLabel, moduleTitle, presets = [], testid }) {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setDraft((tenant?.[labelKey] || "").trim()); }, [open, tenant, labelKey]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/tenants/${tenantId}/config`, { [labelKey]: draft.trim() });
      await refreshTenants?.();
      toast.success(draft.trim() ? `Module renamed to "${draft.trim()}"` : "Module name reset to default");
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };
  return (
    <ModuleSettingsSheet open={open} onOpenChange={onOpenChange} testid={testid || "module-settings-sheet"}
      title={`${moduleTitle} settings`}
      description={`Configure how this module appears to your ${V.lc("org")}.`}
      onSave={save} saving={saving}>
      <div>
        <label className="text-sm font-semibold text-ink">Module name</label>
        <p className="text-xs text-muted-foreground mt-0.5 mb-2">What this section is called for your {V.lc("org")} — in the sidebar, page titles and the {V.lc("person")} portal.</p>
        <input data-testid={`${testid || "module"}-label-input`} value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={40}
          placeholder={defaultLabel}
          className="w-full rounded-xl border border-sand-200 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
        {presets.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {presets.map((s) => (
              <button key={s} type="button" onClick={() => setDraft(s)}
                className="rounded-full border border-sand-200 bg-sand-50 px-2.5 py-1 text-xs font-medium text-ink/60 hover:border-ink transition-colors">{s}</button>
            ))}
          </div>
        )}
        <p className="text-[11px] text-ink/45 mt-2">Leave blank to use the default (<span className="font-semibold">{defaultLabel}</span>).</p>
      </div>
    </ModuleSettingsSheet>
  );
}
